import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacilityToday } from "@/hooks/use-facility-today";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { optionalDay, KEEP_PLACE } from "@/lib/search";
import { BoardCard } from "./-components/board-card";
import { ChannelChart } from "./-components/channel-chart";
import { DateWindow, daysAgo } from "./-components/date-window";
import { HeadlineTiles } from "./-components/headline-tiles";
import { HourHeatmap } from "./-components/hour-heatmap";
import { ImpactTiles } from "./-components/impact";
import { WhatHappened } from "@/routes/_authenticated/_front-desk/sarah/analytics/-components/what-happened";
import { WhenCallsCome } from "@/routes/_authenticated/_front-desk/sarah/analytics/-components/when-calls-come";
import { PhoneCard } from "./-components/phone-card";
import { Resolution } from "./-components/resolution";
import { ShelbyFunnel } from "./-components/shelby-funnel";
import { TextingCard } from "./-components/texting-card";
import { TopicBars } from "./-components/topic-bars";

export const Route = createFileRoute("/_authenticated/_front-desk/sarah/analytics/")({
  validateSearch: (search: Record<string, unknown>): { from?: string; to?: string } => ({
    from: optionalDay(search.from),
    to: optionalDay(search.to),
  }),
  component: FrontDeskAnalytics,
});

/** The whole front desk over a window: phone, texts, Shelby, follow-up and the board. */
function FrontDeskAnalytics() {
  const { facilityId } = useFacility();
  const today = useFacilityToday(facilityId);
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const window = { from: params.from ?? daysAgo(today, 30), to: params.to ?? today };
  const setWindow = (next: { from: string; to: string }) =>
    void navigate({
      search: {
        from: next.from === daysAgo(today, 30) ? undefined : next.from,
        to: next.to === today ? undefined : next.to,
      },
      ...KEEP_PLACE,
    });
  const input = { facilityId, ...window };

  const overview = useQuery(orpc.frontDesk.calls.overview.queryOptions({ input }));

  /* Impact is one building's, never a filter: it is priced at an hourly rate
     the building sets, and there is no honest answer to what an hour costs
     across all of them. */
  const oneBuilding = facilityId !== "all";
  const impact = useQuery({
    ...orpc.frontDesk.calls.impact.queryOptions({ input: { ...input, facilityId } }),
    enabled: oneBuilding,
  });
  const resolution = useQuery({
    ...orpc.frontDesk.calls.resolution.queryOptions({ input: { ...input, facilityId } }),
    enabled: oneBuilding,
  });

  return (
    <>
      <PageHeader
        title="Front desk analytics"
        meta="Everything Sarah and Shelby handled, what is still open, and how it compares with the window before."
      />
      <DateWindow today={today} from={window.from} to={window.to} onChange={setWindow} />

      {overview.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      ) : overview.isError ? (
        <ReadFailed
          message="Could not read the front desk's figures."
          error={overview.error}
          onRetry={() => void overview.refetch()}
          retrying={overview.isFetching}
        />
      ) : (
        <>
          <HeadlineTiles overview={overview.data} />
          <ChannelChart overview={overview.data} />
          {/* On a phone the cards keep to the screen, so the heatmap scrolls inside its own card
              as it was built to, instead of pushing the page sideways. */}
          <div className="grid gap-3 max-md:[&>*]:min-w-0 xl:grid-cols-2">
            <HourHeatmap overview={overview.data} />
            <TopicBars overview={overview.data} />
          </div>
          <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-4">
            <PhoneCard overview={overview.data} />
            <TextingCard overview={overview.data} />
            {overview.data.shelby ? <ShelbyFunnel shelby={overview.data.shelby} /> : null}
            <BoardCard overview={overview.data} />
          </div>
        </>
      )}

      {oneBuilding ? (
        <>
          <h2 className="pt-2 text-lg font-semibold">What the phone would have cost a person</h2>
          {impact.isError ? (
            <ReadFailed
              message="Could not work out what these calls would have cost."
              error={impact.error}
              onRetry={() => void impact.refetch()}
              retrying={impact.isFetching}
            />
          ) : (
            <ImpactTiles impact={impact.data} />
          )}
          {impact.data ? (
            <div className="grid gap-3 lg:grid-cols-2">
              <WhenCallsCome impact={impact.data} />
              <WhatHappened impact={impact.data} />
            </div>
          ) : null}
          {resolution.isError ? (
            <ReadFailed
              message="Could not read what happened to these calls afterwards."
              error={resolution.error}
              onRetry={() => void resolution.refetch()}
              retrying={resolution.isFetching}
            />
          ) : resolution.data ? (
            <Resolution resolution={resolution.data} />
          ) : null}
        </>
      ) : (
        <p className="text-muted-foreground text-sm">
          Choose a building to see what its calls would have cost a person to handle, and how fast
          the follow-up was closed. The rate is each building&rsquo;s own, so there is no figure for
          all of them at once.
        </p>
      )}
    </>
  );
}
